/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  NgZone,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { IdentityUser } from '@model/identity';
import { GetIdentityUserUseCase, FullLogoutUseCase } from '@usecase/identity';
import { OptionItem } from '@view/shared/molecules/option-item/option-item';
import { OptionListComponent } from '@view/shared/organisms/option-list/option-list.component';
import { TopBarComponent } from '@view/shared/organisms/top-bar/top-bar.component';
import { getIdentityDi } from '@cfg/security/identity-di';
import { CommonModule } from '@angular/common';
import { IdentityOperations } from '@model/identity/operations';
import { RouteService } from '@helpers/routing/router.service';
import { AppTranslationService } from '@service/translation/app-translation.service';
import { AppSplashComponent } from '../splash/splash.component';
import { SidebarComponent } from '../../organisms/sidebar/sidebar.component';
import { AppNotificationService } from '@service/notification/app-notification.service';

// TEMP-SIDEBAR-DIAG: quitar cuando finalice el diagnostico.
const TEMP_SIDEBAR_DIAG = true;

/**
 * Componente que renderiza el layout de la aplicación
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-layout',
  imports: [
    RouterModule,
    CommonModule,
    TopBarComponent,
    OptionListComponent,
    AppSplashComponent,
    SidebarComponent
  ],
  providers: [getIdentityDi],
  template: `
    <div class="flex flex-col min-h-screen">
      <section>
        <app-top-bar
          [username]="user().fullName"
          [lastAccess]="user().lastAccess"
          (menuStateChange)="onMenuStateChange($event)"
          (userMenuStateChange)="onUserMenuStateChange($event)"
        ></app-top-bar>
        <!-- userMenu -->
        <div class="relative">
          <div
            class="absolute right-0 w-52 bg-white app-shadow--basic app-animate-all--ease"
            [ngClass]="{
              'top-0 z-10 opacity-100': isUserMenuOpen(),
              'opacity-0 -top-10 -z-10': !isUserMenuOpen()
            }"
          >
            <app-option-list
              id="userMenu"
              [items]="userMenu()"
              (itemClick)="onUserMenuItemClick($event)"
            ></app-option-list>
          </div>
        </div>
      </section>
      <div class="flex flex-1 relative">
        <!-- sidebar -->
        <div
          #sidebar
          class="rk-container left-0 top-0 w-64 pt-2 app-shadow--basic app-animate-all--ease z-10"
          [ngClass]="{
            'w-64': isMenuOpen(),
            'w-20': !isMenuOpen()
          }"
        >
          <app-sidebar
            id="appMenu"
            [items]="menuItems()"
            [selectable]="true"
            [onlyIcons]="!isMenuOpen()"
            (itemClick)="onMenuItemClicked($event)"
          ></app-sidebar>
        </div>
        <!-- main -->
        <div class="flex-1 overflow-y-auto app-animate-all--ease min-h-0">
          <router-outlet></router-outlet>
        </div>
      </div>
    </div>
    <app-splash [bgClass]="'bg-white'"></app-splash>
  `
})
export class LayoutComponent implements OnInit {
  @ViewChild('sidebar') sidebar?: ElementRef;

  public menuActive = false;
  /** caso de uso para obtener el usuario */
  usecase = inject(GetIdentityUserUseCase);
  /** caso de uso para cerrar sesión */
  logoutUseCase = inject(FullLogoutUseCase);
  /** servicio de traducción */
  translator = inject(AppTranslationService);
  /** servicio de notificaciones */
  notificationService = inject(AppNotificationService);
  /** servicio de ruteo */
  router = inject(RouteService);
  /** zona de Angular */
  ngZone = inject(NgZone);

  /** opciones de menú del usuario */
  userMenu = signal<OptionItem[]>([]);

  user = signal<IdentityUser>({} as IdentityUser);
  userMenuState = signal('closed');
  menuItems = signal<OptionItem[]>([]);
  menuState = signal('closed');

  /**
   * Inicialización del componente, se obtiene el usuario y se construye el menú
   */
  ngOnInit(): void {
    // Obtenemos el usuario y validamos la sesión
    this.usecase.execute().subscribe({
      next: (user) => {
        // Validamos si hay conflicto de sesión
        const sessionConflict = this.checkSessionConflict(user.userName);

        if (sessionConflict.hasConflict) {
          // Detectamos un conflicto de sesión
          this.handleSessionConflict(
            sessionConflict.currentUser!,
            sessionConflict.storedUser!
          );
        } else {
          // No hay conflicto, mostramos los datos del usuario
          this.user.set(user);
          this.prepareAppMenu(user);
          this.prepareUserMenu(user);
        }
      },
      error: () => {
        this.user.set({} as IdentityUser);
        this.menuItems.set([]);
        this.userMenu.set([]);
      }
    });

    this.ngZone.runOutsideAngular(() => {
      const elem = this.sidebar?.nativeElement as HTMLElement;
      if (elem) {
        elem.addEventListener('mouseenter', () => {
          this.onMenuStateChange('opened');
        });
        elem.addEventListener('mouseleave', () => {
          this.onMenuStateChange('closed');
        });
      }
    });
  }

  /**
   * Verifica si hay conflicto de sesión
   * @param currentUserName Usuario actual autenticado
   * @returns Resultado de la validación
   */
  private checkSessionConflict(currentUserName: string): {
    hasConflict: boolean;
    currentUser?: string;
    storedUser?: string;
  } {
    const storedUserName = this.getStoredUser();

    // Si no hay usuario almacenado, guardamos el actual
    if (!storedUserName) {
      this.storeUser(currentUserName);
      return { hasConflict: false, currentUser: currentUserName };
    }

    // Verificamos si hay conflicto
    const hasConflict = storedUserName !== currentUserName;

    return {
      hasConflict,
      currentUser: currentUserName,
      storedUser: storedUserName
    };
  }

  /**
   * Obtiene el usuario almacenado en localStorage
   */
  private getStoredUser(): string | null {
    try {
      return localStorage.getItem('mur_logged_user');
    } catch (error) {
      console.error('Error al obtener usuario del localStorage:', error);
      return null;
    }
  }

  /**
   * Almacena el usuario en localStorage
   */
  private storeUser(userName: string): void {
    try {
      localStorage.setItem('mur_logged_user', userName);
    } catch (error) {
      console.error('Error al almacenar usuario en localStorage:', error);
    }
  }

  /**
   * Maneja el conflicto de sesión detectado
   * @param currentUser Usuario actual autenticado
   * Limpia el usuario almacenado en localStorage
   */
  private clearStoredUser(): void {
    try {
      localStorage.removeItem('mur_logged_user');
    } catch (error) {
      console.error('Error al limpiar usuario del localStorage:', error);
    }
  }

  /**
   * Maneja el conflicto de sesión detectado
   * @param currentUser Usuario actual autenticado
   * @param storedUser Usuario almacenado en la sesión local
   */
  private handleSessionConflict(currentUser: string, storedUser: string): void {
    // Mostramos la notificación de advertencia
    this.notificationService.warn('security.session-conflict-message', {
      currentUser,
      storedUser
    });

    // Esperamos 5 segundos y cerramos sesión automáticamente
    setTimeout(() => {
      this.clearStoredUser();
      this.logoutUseCase.execute();
    }, 5000);
  }

  /**
   * Se ejecuta cuando el estado del menú cambia
   * @param event puede ser opened o closed
   */
  onMenuStateChange(event: string) {
    this.menuState.set(event);
  }

  /**
   * Se ejecuta cuando el estado del menú de usuario cambia
   * @param event puede ser opened o closed
   */
  onUserMenuStateChange(event: string) {
    this.userMenuState.set(event);
  }

  /**
   * Se ejecuta cuando se hace click en un item del menú de usuario
   * @param item item del menú
   */
  onUserMenuItemClick(item: OptionItem) {
    if (item.id === 'logout') {
      this.clearStoredUser();
      this.logoutUseCase.execute();
    } else {
      console.warn('Opción de menú no soportada:', item);
    }
  }

  /**
   * Se ejecuta cuando se hace click en un item del menú sidebar
   * @param item item del menú
   */
  onMenuItemClicked(item: OptionItem) {
    console.log('[DEBUG] Sidebar click → id:', item.id, '| title:', item.title, '| path:', item.path);
    this.router.navigateTo(item.path);
    //this.menuState.set('closed');
  }

  /**
   * Determina si el menú de usuario está abierto
   */
  isUserMenuOpen = computed(() => this.userMenuState() === 'opened');

  isMenuOpen = computed(() => this.menuState() === 'opened');

  /**
   * Construye el menú de la aplicación
   * @param {IdentityUser} user usuario
   */
  private prepareAppMenu(user: IdentityUser) {
    const appMenu = IdentityOperations.getMenu(user);

    if (TEMP_SIDEBAR_DIAG) {
      const rootItems = appMenu?.[0]?.menuItems ?? [];
      const childrenTotal = rootItems.reduce(
        (total, current) => total + (current.children?.length ?? 0),
        0
      );

      console.debug('[TEMP-SIDEBAR-DIAG][Layout.prepareAppMenu] root items:', rootItems.length);
      console.debug(
        '[TEMP-SIDEBAR-DIAG][Layout.prepareAppMenu] children total:',
        childrenTotal
      );
    }

    // Validar si menuItems está vacío o es null
    if (!appMenu || !appMenu[0] || !appMenu[0].menuItems || appMenu[0].menuItems.length === 0) {
      this.router.navigateTo('/access-denied');
      return;
    }

    this.menuItems.set(
      appMenu[0].menuItems.map((item) => {
        return {
          id: item.cdresource.toString(),
          path: item.contextpath,
          title: item.name,
          icon: item.image,
          active: false,
          children: item.children.map((child) => {
            return {
              id: child.cdresource.toString(),
              path: child.contextpath,
              title: child.name,
              icon: child.image,
              active: false
            };
          })
        };
      })
    );

  }

  /**
   * Construye el menú de usuario
   * @param {IdentityUser} user usuario
   */
  private prepareUserMenu(user: IdentityUser) {
    this.userMenu.set([
      {
        id: 'logout',
        path: user.fullUrlLogout,
        title: this.translator.get('security.logout'),
        icon: 'user'
      }
    ]);
  }
}
